import { AddOn, Experience, FeatureRole, Form, PurchaseRuleLink, Seller } from "types";
import { isAutoAcceptEnabledForExperience } from "./Experience";
import { hasFeature } from "./Seller";

export interface DuplicateSuccessData {
    experienceId: string;
    items: Array<{ selected: boolean; key: string; label: string }>;
}

interface getAvailableFeaturesToDuplicateProps {
    originalExperience: Experience;
    seller: Seller;
    addOns: AddOn[] | null;
    form: Form | undefined;
    feePurchaseRuleLinks: PurchaseRuleLink[] | null;
    virtualMeeting: { isGranted: boolean; enabled?: boolean };
}

export const getAvailableFeaturesToDuplicate = ({
    originalExperience,
    seller,
    addOns,
    form,
    feePurchaseRuleLinks,
    virtualMeeting,
}: getAvailableFeaturesToDuplicateProps) =>
    ITEMS_TO_DUPLICATE.filter((item) => {
        switch (item.key) {
            case AllowedDuplicateItems.SCHEDULES:
                return originalExperience.schedules.length > 0;
            case AllowedDuplicateItems.ADDONS:
                return addOns && addOns.length > 0;
            case AllowedDuplicateItems.GUIDES:
                return item.key in originalExperience && hasFeature(seller, FeatureRole.GUIDE_MANAGER);
            case AllowedDuplicateItems.WAITLIST:
                return item.key in originalExperience && hasFeature(seller, FeatureRole.WAITLIST);
            case AllowedDuplicateItems.FORMS:
                return !!form;
            case AllowedDuplicateItems.FEES:
                return !!feePurchaseRuleLinks?.length;
            case AllowedDuplicateItems.AUTO_ACCEPT:
                return isAutoAcceptEnabledForExperience(originalExperience, seller);
            case AllowedDuplicateItems.VIRTUAL_MEETING:
                return virtualMeeting?.isGranted && virtualMeeting?.enabled;
            case AllowedDuplicateItems.CHECK_IN:
                return (
                    item.key in originalExperience ||
                    !!originalExperience.firstCheckinExpirationOffset ||
                    !!originalExperience.purchaseExpirationOffset
                );
            case AllowedDuplicateItems.QR_CODE:
                return item.key in originalExperience && !!originalExperience.qrCodePreference?.enabled;
            case AllowedDuplicateItems.WAIVERS:
                return item.key in originalExperience && !!originalExperience.waiverPreference?.url;

            default:
                return item.key in originalExperience;
        }
    });

export enum AllowedDuplicateItems {
    SCHEDULES = "schedules",
    ADDONS = "addOns",
    WAIVERS = "waiverPreference",
    GUIDES = "guidePreference",
    REVIEWS = "reviewPreference",
    TRAVELER = "travelerPreference",
    TERMS = "terms",
    WAITLIST = "waitlist",
    QR_CODE = "qrCodePreference",
    CHECK_IN = "checkInPreference",
    FEES = "fees",
    AUTO_ACCEPT = "autoAcceptSettings",
    FORMS = "forms",
    VIRTUAL_MEETING = "virtualMeeting",
}

export enum PreferenceLabels {
    SCHEDULES = "Schedules",
    ADDONS = "Add-ons",
    WAIVERS = "Digital Waivers",
    GUIDES = "Guides",
    EQUIPMENT = "Equipment",
    REVIEWS = "Reviews",
    TRAVELER = "Self Service",
    TERMS = "Terms",
    WAITLIST = "Waitlist",
    QR_CODE = "Tickets",
    CHECK_IN = "Check-in",
    FEES = "Taxes and Fees",
    AUTO_ACCEPT = "Auto Accept Settings",
    FORMS = "Questionnaire",
    EXPERIENCE = "Experience",
    SCHEDULE_PURCHASE_RULES = "Schedule Price Variations",
    DEMOGRAPHICS = "Demographics",
    VIRTUAL_MEETING = "Virtual Meeting",
}

export const ITEMS_TO_DUPLICATE = [
    {
        label: PreferenceLabels.SCHEDULES,
        key: AllowedDuplicateItems.SCHEDULES,
    },
    {
        label: PreferenceLabels.ADDONS,
        key: AllowedDuplicateItems.ADDONS,
    },
    {
        label: PreferenceLabels.WAIVERS,
        key: AllowedDuplicateItems.WAIVERS,
    },
    {
        label: PreferenceLabels.GUIDES,
        key: AllowedDuplicateItems.GUIDES,
    },
    {
        label: PreferenceLabels.REVIEWS,
        key: AllowedDuplicateItems.REVIEWS,
    },
    {
        label: PreferenceLabels.TRAVELER,
        key: AllowedDuplicateItems.TRAVELER,
    },
    {
        label: PreferenceLabels.TERMS,
        key: AllowedDuplicateItems.TERMS,
    },
    {
        label: PreferenceLabels.WAITLIST,
        key: AllowedDuplicateItems.WAITLIST,
    },
    {
        label: PreferenceLabels.QR_CODE,
        key: AllowedDuplicateItems.QR_CODE,
    },
    {
        label: PreferenceLabels.CHECK_IN,
        key: AllowedDuplicateItems.CHECK_IN,
    },
    {
        label: PreferenceLabels.FEES,
        key: AllowedDuplicateItems.FEES,
    },
    {
        label: PreferenceLabels.AUTO_ACCEPT,
        key: AllowedDuplicateItems.AUTO_ACCEPT,
    },
    {
        label: PreferenceLabels.FORMS,
        key: AllowedDuplicateItems.FORMS,
    },
    {
        label: PreferenceLabels.VIRTUAL_MEETING,
        key: AllowedDuplicateItems.VIRTUAL_MEETING,
    },
];

export enum ExperienceCheckInPreferences {
    FIRST_CHECKIN_EXPIRATION_OFFSET = "firstCheckinExpirationOffset",
    PURCHASE_EXPIRATION_OFFSET = "purchaseExpirationOffset",
}
